<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard">
        <template v-slot:header>Appeal the identity check</template>
        <template v-slot:subheader>{{ props.brandId }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <div>
                Use this when the details are right but the check still failed; TCR re-checks the brand against the
                documents you attach.
            </div>

            <div data-test="appeal-categories-select">
                <vx-label size="md">Categories</vx-label>
                <vx-selectable
                    v-model="form.categories"
                    multiple
                    size="lg"
                    placeholder="Select what TCR should re-check"
                    :options="categoryOptions"
                    :reduce="(option: TcrBrandAppealCategory) => option.value"
                    :closeOnSelect="false"
                    :loading="null === props.enums"
                    :error="displayFirstError(validate.categories.$errors)"
                >
                    <template v-slot:option="{ option }">
                        <div>{{ option.label }}</div>
                        <div class="text-xs text-slate-500">{{ option.description }}</div>
                    </template>
                </vx-selectable>
            </div>

            <div>
                <vx-label size="md">Explanation</vx-label>
                <vx-input
                    v-model="form.explanation"
                    :rows="4"
                    size="lg"
                    data-test="appeal-explanation-input"
                    :error="displayFirstError(validate.explanation.$errors)"
                ></vx-input>
                <vx-char-counter :content="form.explanation" :max="EXPLANATION_MAX_LENGTH" />
            </div>

            <div>
                <vx-label size="md">Evidence</vx-label>
                <p class="text-sm text-slate-500 mb-2">
                    PDFs and images, scaled down to fit; the IRS letter showing the EIN and legal name, for instance
                </p>
                <input
                    type="file"
                    ref="evidenceInput"
                    multiple
                    class="opacity-0 absolute w-1 h-1"
                    :accept="EVIDENCE_ACCEPT"
                    aria-hidden="true"
                    :disabled="preparing"
                    data-test="appeal-evidence-input"
                    @change="onEvidenceChange"
                />
                <vx-button
                    @click="evidenceInput?.click()"
                    type="button"
                    size="sm"
                    outline
                    color="muted"
                    data-test="choose-evidence"
                    :disabled="preparing"
                >
                    Choose files <font-awesome-icon widthAuto :icon="faUpload" />
                </vx-button>
                <div v-if="preparing" class="text-sm text-slate-500 mt-1" data-test="appeal-evidence-preparing">
                    Preparing…
                </div>
                <div v-if="evidenceErrors.length" class="text-sm text-rose-600 mt-1" data-test="appeal-evidence-error">
                    <div v-for="(error, index) in evidenceErrors" :key="index">{{ error }}</div>
                </div>
                <ul v-if="files.length" class="text-slate-500 mt-1" data-test="appeal-evidence-ready">
                    <li v-for="(file, index) in files" :key="index">
                        {{ file.name }} · {{ Math.round(file.size / 1024) }} KB
                        <span v-if="uploaded.has(file)"> · uploaded</span>
                    </li>
                </ul>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-appeal"
                    :disabled="preparing"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Submit appeal
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxCharCounter, VxInput, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { preparePdf } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationDocument';
import { IMAGE_ACCEPT, prepareImage } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationImage';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type {
    TcrBrandAppealCategory,
    TcrBrandAppealCreate,
    TcrEnums,
} from '~/services/adminTenDlcRegistrationClient.types';
import { catchRequestError } from '~/utils/request';
import { displayFirstError, validateRules } from '~/utils/validation';

type AppealForm = Pick<TcrBrandAppealCreate, 'categories' | 'explanation'>;

// UploadTcrBrandAppealEvidenceRequest::EVIDENCE_MIMES.
const PDF_TYPE = 'application/pdf';
const IMAGE_TYPES = IMAGE_ACCEPT.split(',');
const EVIDENCE_ACCEPT = [...IMAGE_TYPES, PDF_TYPE].join(',');
// AppealTcrBrandRequest::EXPLANATION_MAX_LENGTH.
const EXPLANATION_MAX_LENGTH = 1024;

const props = defineProps<{ brandId: string; enums: TcrEnums | null }>();
const emit = defineEmits<{ submitted: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();

const emptyForm = (): AppealForm => ({ categories: [], explanation: '' });

const form = ref<AppealForm>(emptyForm());
const evidenceInput = ref<HTMLInputElement>();
const files = ref<File[]>([]);
const evidenceErrors = ref<string[]>([]);
const preparing = ref(false);
// The uuids TCR already holds, so a retry after a failed upload or appeal sends only the rest.
const uploaded = ref(new Map<File, string>());
const submitting = ref(false);

const validate = useVuelidate(
    {
        categories: { required },
        explanation: { maxLength: maxLength(EXPLANATION_MAX_LENGTH) },
    },
    form,
    { $externalResults }
);

const categoryOptions = computed(() => props.enums?.brand_appeal_categories ?? []);

const prepareEvidence = async (file: File): Promise<File | string> => {
    if (PDF_TYPE !== file.type && !IMAGE_TYPES.includes(file.type)) {
        return `${file.name} is not a PDF, PNG, JPEG or WebP`;
    }

    try {
        return await (PDF_TYPE === file.type ? preparePdf(file) : prepareImage(file));
    } catch (e) {
        return `${file.name}: ${e instanceof Error ? e.message : 'The file could not be prepared.'}`;
    }
};

const onEvidenceChange = async (event: Event) => {
    const selected = Array.from((event.target as HTMLInputElement).files ?? []);
    files.value = [];
    evidenceErrors.value = [];
    preparing.value = true;

    const prepared = await Promise.all(selected.map(prepareEvidence));

    files.value = prepared.filter((result): result is File => result instanceof File);
    evidenceErrors.value = prepared.filter((result): result is string => 'string' === typeof result);
    preparing.value = false;
};

watch(visible, (open) => {
    if (open) {
        form.value = emptyForm();
        files.value = [];
        evidenceErrors.value = [];
        uploaded.value = new Map();
        $externalResults.value = {};
        validate.value.$reset();
    }
});

const confirm = async () => {
    if (submitting.value || preparing.value) {
        return;
    }

    submitting.value = true;
    validate.value.$reset();

    if (!(await validateRules(validate))) {
        submitting.value = false;
        return;
    }

    try {
        for (const file of files.value) {
            if (!uploaded.value.has(file)) {
                const { data } = await adminTenDlcRegistrationClient.uploadBrandAppealEvidence(props.brandId, file);
                uploaded.value.set(file, data.uuid);
            }
        }
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
        submitting.value = false;
        return;
    }

    try {
        await adminTenDlcRegistrationClient.appealBrand(props.brandId, {
            categories: form.value.categories,
            explanation: form.value.explanation,
            attachment_uuids: files.value
                .map((file) => uploaded.value.get(file))
                .filter((uuid): uuid is string => undefined !== uuid),
        });

        toasted.success('Appeal submitted.');
        emit('submitted');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
